<!--
 * @Author: Christion.Young
 * @Date: 2025-09-08 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-09-08 14:00:00
 * @Description: 高级预约组件 - 支持多种预约类型和智能时间选择
-->
<template>
  <div class="advanced-appointment">
    <!-- 预约类型切换 -->
    <div class="appointment-type-tabs">
      <div class="type-tab-header">
        <h4><i class="el-icon-time"></i>预约时间</h4>
        <div class="order-type-info">
          <span class="order-type-badge" :class="orderTypeClass">
            {{ orderTypeText }}
          </span>
        </div>
      </div>
    </div>

    <!-- 单一接单模式 -->
    <div v-if="serviceInfo.orderType === '1'" class="multiple-time-selection">
      <!-- 全天服务模式 (intervalTime === '4') -->
      <div v-if="serviceInfo.intervalTime === '4'" class="full-day-selection">
        <div class="full-day-info">
          <div class="info-card">
            <i class="el-icon-date"></i>
            <div class="info-content">
              <h5>全天服务</h5>
              <p>此服务按整天计费，请选择服务日期（可多选）</p>
            </div>
          </div>
        </div>

        <!-- 日期横向滚动选择（全天模式） -->
        <div class="date-scroll-container">
          <div class="date-scroll-wrapper">
            <div
              v-for="(day, dayIndex) in timeSlots"
              :key="dayIndex"
              class="date-item"
              :class="{
                active: isFullDaySelected(day.date),
                disabled: !day.isWeekday,
              }"
              @click="toggleFullDaySelection(day)"
            >
              <div class="date-number">{{ day.date.split("-")[1] }}</div>
              <div class="date-week">{{ day.week }}</div>
              <div class="date-status">
                <span v-if="!day.isWeekday" class="status-closed">休息</span>
                <span
                  v-else-if="isFullDaySelected(day.date)"
                  class="status-selected"
                  >已选</span
                >
                <span v-else class="status-available">可选</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 已选择的日期列表 -->
        <div v-if="selectedFullDays.length > 0" class="selected-times-list">
          <h5>已选择的服务日期</h5>
          <div class="selected-times-grid">
            <div
              v-for="(day, index) in selectedFullDays"
              :key="index"
              class="selected-time-item"
            >
              <span class="time-info">{{ day }} 全天</span>
              <button class="remove-btn" @click="removeFullDay(index)">
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 时间段服务模式 (intervalTime !== '4') -->
      <div v-else class="time-slot-selection">
        <div class="time-selection-header">
          <div class="selection-info">
            <span class="selected-count"
              >已选择 {{ selectedTimeSlots.length }} 个时间段</span
            >
            <span class="duration-info">服务时长：{{ intervalTimeText }}</span>
          </div>
          <button
            v-if="selectedTimeSlots.length > 0"
            class="clear-selection-btn"
            @click="clearAllSelections"
          >
            <i class="el-icon-delete"></i>
            清空选择
          </button>
        </div>

        <!-- 日期横向滚动选择 -->
        <div class="date-scroll-container">
          <div class="date-scroll-wrapper">
            <div
              v-for="(day, dayIndex) in timeSlots"
              :key="dayIndex"
              class="date-item"
              :class="{
                active: day.isOk,
                disabled: !day.isWeekday || day.time.length === 0,
              }"
              @click="selectDate(day)"
            >
              <div class="date-number">{{ day.date.split("-")[1] }}</div>
              <div class="date-week">{{ day.week }}</div>
              <div class="date-status">
                <span v-if="!day.isWeekday" class="status-closed">休息</span>
                <span
                  v-else-if="
                    day.time.filter((t) => t.isTimeQuantum).length === 0
                  "
                  class="status-full"
                  >约满</span
                >
                <span v-else class="status-available"
                  >{{ day.time.filter((t) => t.isTimeQuantum).length }}个</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- 时间段网格选择 -->
        <div
          v-if="selectedDateTimeSlots.length > 0"
          class="time-grid-container"
        >
          <div class="time-grid-header">
            <h5>{{ timeQuantumData }} 可选时间</h5>
            <div class="time-legend">
              <span class="legend-item available"
                ><span class="legend-dot"></span>可选</span
              >
              <span class="legend-item booked"
                ><span class="legend-dot"></span>已约</span
              >
              <span class="legend-item past"
                ><span class="legend-dot"></span>已过</span
              >
            </div>
          </div>
          <div class="time-grid">
            <div
              v-for="(timeSlot, timeIndex) in selectedDateTimeSlots"
              :key="timeIndex"
              class="time-slot"
              :class="{
                available: timeSlot.isTimeQuantum,
                booked: timeSlot.isBooked,
                past: timeSlot.isPast,
                selected: isTimeSlotSelected(timeQuantumData, timeSlot.value),
              }"
              @click="toggleTimeSlot(timeQuantumData, timeSlot)"
            >
              <span class="time-text">{{ timeSlot.value }}</span>
              <i
                v-if="isTimeSlotSelected(timeQuantumData, timeSlot.value)"
                class="el-icon-check selected-icon"
              ></i>
            </div>
          </div>
        </div>

        <!-- 已选择的时间段列表 -->
        <div v-if="selectedTimeSlots.length > 0" class="selected-times-list">
          <h5>已选择的时间段</h5>
          <div class="selected-times-grid">
            <div
              v-for="(slot, index) in selectedTimeSlots"
              :key="index"
              class="selected-time-item"
            >
              <span class="time-info"
                >日期：{{ slot.date }} 时间：{{ slot.time }}</span
              >
              <button class="remove-btn" @click="removeTimeSlot(index)">
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 同时多单模式：单个时间选择 -->
    <div
      v-else-if="serviceInfo.orderType === '2'"
      class="single-time-selection"
    >
      <div class="single-selection-form">
        <div class="form-row">
          <div class="form-group">
            <label>预约日期</label>
            <el-date-picker
              v-model="singleAppointment.date"
              type="date"
              placeholder="选择日期"
              :picker-options="datePickerOptions"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              @change="onSingleDateChange"
            >
            </el-date-picker>
          </div>
          <div class="form-group">
            <label>预约时间</label>
            <el-select
              v-model="singleAppointment.time"
              :placeholder="
                isTimeSelectEnabled
                  ? '选择时间'
                  : '请先选择日期或该日期无可用时间'
              "
              :disabled="!isTimeSelectEnabled"
            >
              <el-option
                v-for="time in availableTimesForSingleDate"
                :key="time.value"
                :label="time.value"
                :value="time.value"
                :disabled="!time.isTimeQuantum"
              >
                <span>{{ time.value }}</span>
                <span v-if="time.isBooked" style="color: #f56c6c; float: right"
                  >已约</span
                >
                <span
                  v-else-if="time.isPast"
                  style="color: #909399; float: right"
                  >已过</span
                >
              </el-option>
            </el-select>
          </div>
        </div>
      </div>
    </div>

    <!-- 预约摘要 -->
    <div class="appointment-summary">
      <div class="summary-header">
        <h5><i class="el-icon-document"></i>预约摘要</h5>
      </div>
      <div class="summary-content">
        <div v-if="appointmentSummary.times.length > 0" class="summary-times">
          <strong>预约时间：</strong>
          <div class="times-list">
            <span
              v-for="(time, index) in appointmentSummary.times"
              :key="index"
              class="time-tag"
            >
              {{ time }}
            </span>
          </div>
        </div>
        <div class="summary-price">
          <span class="price-label">服务费用：</span>
          <span class="price-value"
            >¥{{ appointmentSummary.servicePrice }}</span
          >
        </div>
        <div v-if="appointmentSummary.quantity > 1" class="summary-quantity">
          <span class="quantity-label">服务次数：</span>
          <span class="quantity-value"
            >{{ appointmentSummary.quantity }} 次</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { generateTimeSlots } from "@/utils/timeSlotsUtil";

export default {
  name: "AdvancedAppointment",
  props: {
    serviceInfo: {
      type: Object,
      required: true,
    },
    appointmentParameter: {
      type: Object,
      default: () => ({
        date: "",
        time: "",
        dateList: [],
      }),
    },
    weeklyTimes: {
      type: Array,
      default: () => [],
    },
    haveMadeAnAppointment: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      timeSlots: [], // 主数据源 - 对应架构中的 timeSlotsData
      selectedDate: "", // 当前选中的日期
      selectedTimeSlots: [], // 已选择的时间段列表（用于数据同步）
      selectedFullDays: [], // 全天服务选择的日期列表
      timeQuantum: [], // 当前日期的时间段数据 - 对应架构中的 timeQuantum
      timeQuantumData: "", // 当前选中的日期标识 - 对应架构中的 timeQuantumData
      singleAppointment: {
        date: "",
        time: "",
      },
      // 字典选项
      intervalTimeOptions: [
        { value: "1", label: "60分钟" },
        { value: "2", label: "120分钟" },
        { value: "3", label: "180分钟" },
        { value: "4", label: "一天" },
        { value: "5", label: "一次/项" },
      ],
      orderTypeOptions: [
        { value: "1", label: "单一接单" },
        { value: "2", label: "同时多单" },
      ],
    };
  },
  computed: {
    // 接单类型文本
    orderTypeText() {
      const option = this.orderTypeOptions.find(
        (opt) => opt.value === this.serviceInfo.orderType
      );
      return option ? option.label : "未知";
    },

    // 接单类型样式类
    orderTypeClass() {
      return this.serviceInfo.orderType === "1"
        ? "single-order"
        : "multiple-order";
    },

    // 服务时长文本
    intervalTimeText() {
      const option = this.intervalTimeOptions.find(
        (opt) => opt.value === this.serviceInfo.intervalTime
      );
      return option ? option.label : "未知";
    },

    // 当前选择日期的时间槽 - 使用 timeQuantum
    selectedDateTimeSlots() {
      return this.timeQuantum;
    },

    // 单个预约模式的可用时间
    availableTimesForSingleDate() {
      // 只有在同时多单模式下才使用此计算属性
      if (this.serviceInfo.orderType !== "2") {
        console.log("availableTimesForSingleDate: 非同时多单模式，返回空数组");
        return [];
      }

      if (!this.singleAppointment.date) {
        console.log("availableTimesForSingleDate: 没有选择日期，返回空数组");
        return [];
      }

      console.log("availableTimesForSingleDate: 开始生成可用时间", {
        选择日期: this.singleAppointment.date,
        接单模式: this.serviceInfo.orderType,
      });

      // 为同时多单模式单独生成可用时间（不依赖 weeklyTimes）
      const availableTimes = this.generateSingleDateAvailableTimesForMultiple(
        this.singleAppointment.date
      );

      console.log("availableTimesForSingleDate: 最终结果", {
        总时间段: availableTimes.length,
        可选时间段: availableTimes.filter((t) => t.isTimeQuantum).length,
        详细数据: availableTimes,
      });

      return availableTimes;
    },

    // 时间选择器是否可用
    isTimeSelectEnabled() {
      if (this.serviceInfo.orderType !== "2") return false;
      if (!this.singleAppointment.date) return false;

      const available = this.availableTimesForSingleDate;
      const hasSelectableTimes = available.some((t) => t.isTimeQuantum);

      console.log("时间选择器状态检查:", {
        选择日期: this.singleAppointment.date,
        总时间数: available.length,
        可选时间数: available.filter((t) => t.isTimeQuantum).length,
        是否可用: hasSelectableTimes,
      });

      return hasSelectableTimes;
    },

    // 日期选择器配置
    datePickerOptions() {
      return {
        disabledDate: (time) => {
          // 不能选择过去的日期
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return time.getTime() < today.getTime();
        },
      };
    },

    // 预约摘要
    appointmentSummary() {
      const summary = {
        times: [],
        servicePrice: 0,
        quantity: 0,
      };

      if (this.serviceInfo.orderType === "1") {
        // 单一接单模式
        if (this.serviceInfo.intervalTime === "4") {
          // 全天服务
          summary.times = this.selectedFullDays.map((day) => `${day} 全天`);
          summary.quantity = this.selectedFullDays.length;
          summary.servicePrice = (
            parseFloat(this.serviceInfo.servicePrice) * summary.quantity
          ).toFixed(2);
        } else {
          // 时间段服务
          summary.times = this.selectedTimeSlots.map(
            (slot) => `${slot.date} ${slot.time}`
          );
          summary.quantity = this.selectedTimeSlots.length;
          summary.servicePrice = (
            parseFloat(this.serviceInfo.servicePrice) * summary.quantity
          ).toFixed(2);
        }
      } else if (this.serviceInfo.orderType === "2") {
        // 同时多单模式
        if (this.singleAppointment.date && this.singleAppointment.time) {
          summary.times = [
            `${this.singleAppointment.date} ${this.singleAppointment.time}`,
          ];
          summary.quantity = 1;
          summary.servicePrice = parseFloat(
            this.serviceInfo.servicePrice
          ).toFixed(2);
        }
      }

      return summary;
    },
  },
  watch: {
    // 监听服务信息变化，重新生成时间槽
    serviceInfo: {
      handler() {
        this.generateAvailableTimeSlots();
      },
      deep: true,
      immediate: true,
    },

    // 监听已预约时间变化
    haveMadeAnAppointment: {
      handler() {
        this.generateAvailableTimeSlots();
      },
      deep: true,
    },

    // 监听工作时间配置变化
    weeklyTimes: {
      handler() {
        this.generateAvailableTimeSlots();
      },
      deep: true,
    },

    // 监听时间槽数据变化（仅单一接单模式）
    timeSlots: {
      handler(newVal) {
        if (this.serviceInfo.orderType !== "1") return;

        console.log("timeSlots 数据变化:", newVal);
      },
      deep: true,
    },

    // 监听单个预约日期变化（仅同时多单模式）
    "singleAppointment.date": {
      handler(newVal, oldVal) {
        if (this.serviceInfo.orderType !== "2") return;

        console.log("singleAppointment.date 变化:", {
          旧值: oldVal,
          新值: newVal,
        });

        if (newVal) {
          this.$nextTick(() => {
            const available = this.availableTimesForSingleDate;
            console.log("日期变化后的可用时间数量:", available.length);
            console.log(
              "可选择的时间段:",
              available.filter((t) => t.isTimeQuantum)
            );
          });
        }
      },
      immediate: true,
    },

    // 同步数据到父组件 - 时间段服务
    selectedTimeSlots: {
      handler(newVal) {
        if (
          this.serviceInfo.orderType === "1" &&
          this.serviceInfo.intervalTime !== "4"
        ) {
          this.$emit("update:appointmentParameter", {
            ...this.appointmentParameter,
            dateList: newVal, // newVal 已经是正确的格式
          });
        }
      },
      deep: true,
    },

    // 同步数据到父组件 - 全天服务
    selectedFullDays: {
      handler(newVal) {
        if (
          this.serviceInfo.orderType === "1" &&
          this.serviceInfo.intervalTime === "4"
        ) {
          this.$emit("update:appointmentParameter", {
            ...this.appointmentParameter,
            dateList: newVal, // 直接使用短格式日期字符串数组
          });
        }
      },
      deep: true,
    },

    singleAppointment: {
      handler(newVal) {
        if (this.serviceInfo.orderType === "2") {
          this.$emit("update:appointmentParameter", {
            ...this.appointmentParameter,
            date: newVal.date,
            time: newVal.time,
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log("AdvancedAppointment 组件已挂载");
    console.log("初始化数据:", {
      serviceInfo: this.serviceInfo,
      weeklyTimes: this.weeklyTimes,
      haveMadeAnAppointment: this.haveMadeAnAppointment,
    });
    // 确保在组件挂载后重新生成时间槽
    this.$nextTick(() => {
      this.generateAvailableTimeSlots();
    });
  },
  methods: {
    /**
     * 生成可用时间槽 - 按照架构文档的 initialize() 逻辑
     */
    generateAvailableTimeSlots() {
      // 只有单一接单模式才需要生成 timeSlots
      if (this.serviceInfo.orderType !== "1") {
        console.log(
          "generateAvailableTimeSlots: 非单一接单模式，跳过timeSlots生成"
        );
        return;
      }

      if (!this.serviceInfo) return;

      console.log("生成时间槽，weeklyTimes:", this.weeklyTimes);
      console.log("已预约时间:", this.haveMadeAnAppointment);

      // 解析工作时间配置 - 使用API返回的正确字段名
      const weekdays = this.weeklyTimes.map((wt) => ({
        weekday: parseInt(wt.weekday), // 确保是数字格式
        workHoursStart: wt.workHoursStart, // 使用正确的字段名
        workHoursEnd: wt.workHoursEnd, // 使用正确的字段名
      }));

      // 解析已预约时间
      const appointments = this.haveMadeAnAppointment.map((app) => ({
        type: app.type || "time",
        date: app.date,
        time: app.time,
      }));

      // 生成时间槽配置
      const config = {
        interval: this.getIntervalMinutes(),
        weekdays: weekdays.length > 0 ? weekdays : undefined,
        timeHorizon: 30,
        HaveMadeAnAppointment: appointments,
      };

      console.log("时间槽配置:", config);

      this.timeSlots = generateTimeSlots(config);

      console.log("生成的时间槽:", this.timeSlots);
      console.log(
        "时间槽详细信息:",
        this.timeSlots.map((slot) => ({
          日期: slot.date,
          星期: slot.week,
          是否工作日: slot.isWeekday,
          时间段数量: slot.time ? slot.time.length : 0,
          可用时间段: slot.time
            ? slot.time.filter((t) => t.isTimeQuantum).length
            : 0,
        }))
      );

      // 按照架构文档：为每个时间槽添加前端交互状态
      this.initializeInteractionStates();

      // 按照架构文档：默认选中第一个可用工作日
      this.setDefaultSelection();
    },

    /**
     * 为单个日期生成可用时间（专用于同时多单模式）
     */
    generateSingleDateAvailableTimes(selectedDate) {
      console.log("generateSingleDateAvailableTimes: 方法开始执行", {
        输入日期: selectedDate,
        工作时间配置: this.weeklyTimes,
        配置长度: this.weeklyTimes ? this.weeklyTimes.length : 0,
      });

      if (!selectedDate || !this.weeklyTimes || this.weeklyTimes.length === 0) {
        console.log("generateSingleDateAvailableTimes: 缺少必要参数", {
          有选择日期: !!selectedDate,
          有工作时间配置: !!this.weeklyTimes,
          工作时间配置长度: this.weeklyTimes ? this.weeklyTimes.length : 0,
        });
        return [];
      }

      console.log("generateSingleDateAvailableTimes: 为单个日期生成时间", {
        选择日期: selectedDate,
        工作时间配置: this.weeklyTimes,
      });

      // 将完整日期转换为Date对象
      const targetDate = new Date(selectedDate);
      const dayOfWeek = targetDate.getDay(); // 0: 周日, 1: 周一, ..., 6: 周六
      const shortDate = this.formatDateToShort(selectedDate);

      console.log("generateSingleDateAvailableTimes: 日期信息", {
        完整日期: selectedDate,
        目标日期对象: targetDate,
        星期几: dayOfWeek,
        短格式日期: shortDate,
      });

      // 查找当天的工作时间配置
      const weekdayConfig = this.weeklyTimes.find(
        (wt) => parseInt(wt.weekday) === dayOfWeek
      );

      console.log("generateSingleDateAvailableTimes: 工作日配置查找", {
        目标星期: dayOfWeek,
        所有配置: this.weeklyTimes.map((wt) => ({
          weekday: wt.weekday,
          parsed: parseInt(wt.weekday),
        })),
        找到的配置: weekdayConfig,
      });

      if (!weekdayConfig) {
        console.log("generateSingleDateAvailableTimes: 该日期不在工作时间内");
        return [];
      }

      console.log(
        "generateSingleDateAvailableTimes: 找到工作时间配置",
        weekdayConfig
      );

      // 解析工作时间范围
      const [startHour, startMinute] = this.parseTime(
        weekdayConfig.workHoursStart
      );
      const [endHour, endMinute] = this.parseTime(weekdayConfig.workHoursEnd);

      console.log("generateSingleDateAvailableTimes: 工作时间范围", {
        开始: `${startHour}:${startMinute}`,
        结束: `${endHour}:${endMinute}`,
        原始开始时间: weekdayConfig.workHoursStart,
        原始结束时间: weekdayConfig.workHoursEnd,
      });

      // 获取该日期的已预约时间
      const appointedTimes = this.haveMadeAnAppointment
        .filter((app) => app.type === "time" && app.date === shortDate)
        .map((app) => app.time);

      console.log(
        "generateSingleDateAvailableTimes: 已预约时间",
        appointedTimes
      );

      // 生成时间段
      const timeSlots = [];
      const now = new Date();

      // 计算当天的开始和结束时间戳
      let currentTime = new Date(targetDate);
      currentTime.setHours(startHour, startMinute, 0, 0);

      const endTime = new Date(targetDate);
      endTime.setHours(endHour, endMinute, 0, 0);

      const interval = this.getIntervalMinutes();

      console.log("generateSingleDateAvailableTimes: 时间生成参数", {
        开始时间对象: currentTime,
        结束时间对象: endTime,
        当前时间: now,
        时间间隔分钟: interval,
      });

      // 生成时间槽
      while (currentTime < endTime) {
        // 修改为 < 而不是 <=，避免包含结束时间
        const hour = currentTime.getHours();
        const minute = currentTime.getMinutes();
        const timeStr = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;

        // 判断是否在工作时间内、未被预约、且时间未过
        // 注意：如果是今天，需要检查时间是否已过；如果是未来日期，所有时间都可用
        const isToday = targetDate.toDateString() === now.toDateString();
        const isPast = isToday && currentTime < now;
        const isBooked = appointedTimes.includes(timeStr);
        const isTimeQuantum = !isBooked && !isPast;

        timeSlots.push({
          value: timeStr,
          isTimeQuantum: isTimeQuantum,
          isBooked: isBooked,
          isPast: isPast,
        });

        // 更新当前时间
        currentTime.setMinutes(currentTime.getMinutes() + interval);
      }

      console.log("generateSingleDateAvailableTimes: 生成的时间段", {
        总数: timeSlots.length,
        可选择数量: timeSlots.filter((t) => t.isTimeQuantum).length,
        时间段: timeSlots,
      });

      return timeSlots;
    },

    /**
     * 为同时多单模式生成单个日期的可用时间
     * 不依赖 weeklyTimes，使用固定的工作时间
     */
    generateSingleDateAvailableTimesForMultiple(selectedDate) {
      console.log("generateSingleDateAvailableTimesForMultiple: 开始执行", {
        输入日期: selectedDate,
      });

      if (!selectedDate) {
        console.log(
          "generateSingleDateAvailableTimesForMultiple: 没有选择日期"
        );
        return [];
      }

      // 将完整日期转换为Date对象
      const targetDate = new Date(selectedDate);
      const shortDate = this.formatDateToShort(selectedDate);

      console.log("generateSingleDateAvailableTimesForMultiple: 日期信息", {
        完整日期: selectedDate,
        目标日期对象: targetDate,
        短格式日期: shortDate,
      });

      // 固定的工作时间：上午9点到下午6点
      const startHour = 9;
      const startMinute = 0;
      const endHour = 18;
      const endMinute = 0;

      console.log("generateSingleDateAvailableTimesForMultiple: 工作时间范围", {
        开始: `${startHour}:${startMinute}`,
        结束: `${endHour}:${endMinute}`,
      });

      // 获取该日期的已预约时间
      const appointedTimes = this.haveMadeAnAppointment
        .filter((app) => app.type === "time" && app.date === shortDate)
        .map((app) => app.time);

      console.log(
        "generateSingleDateAvailableTimesForMultiple: 已预约时间",
        appointedTimes
      );

      // 生成时间段
      const timeSlots = [];
      const now = new Date();

      // 计算当天的开始和结束时间戳
      let currentTime = new Date(targetDate);
      currentTime.setHours(startHour, startMinute, 0, 0);

      const endTime = new Date(targetDate);
      endTime.setHours(endHour, endMinute, 0, 0);

      // 服务时长间隔（分钟）
      const interval = this.getIntervalMinutes();

      console.log("generateSingleDateAvailableTimesForMultiple: 时间生成参数", {
        开始时间对象: currentTime,
        结束时间对象: endTime,
        当前时间: now,
        时间间隔分钟: interval,
      });

      // 生成时间槽
      while (currentTime < endTime) {
        const hour = currentTime.getHours();
        const minute = currentTime.getMinutes();
        const timeStr = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;

        // 判断时间状态
        const isToday = targetDate.toDateString() === now.toDateString();
        const isPast = isToday && currentTime < now;
        const isBooked = appointedTimes.includes(timeStr);
        const isTimeQuantum = !isBooked && !isPast;

        timeSlots.push({
          value: timeStr,
          isTimeQuantum: isTimeQuantum,
          isBooked: isBooked,
          isPast: isPast,
        });

        // 更新当前时间
        currentTime.setMinutes(currentTime.getMinutes() + interval);
      }

      console.log("generateSingleDateAvailableTimesForMultiple: 生成的时间段", {
        总数: timeSlots.length,
        可选择数量: timeSlots.filter((t) => t.isTimeQuantum).length,
        时间段: timeSlots,
      });

      return timeSlots;
    },

    /**
     * 解析时间字符串为小时和分钟
     */
    parseTime(timeStr) {
      // 添加空值检查
      if (!timeStr || typeof timeStr !== "string") {
        console.warn("parseTime: 无效的时间字符串:", timeStr);
        return [0, 0]; // 默认返回 00:00
      }

      try {
        const [hour, minute] = timeStr.split(":").map(Number);
        // 检查解析结果是否有效
        if (isNaN(hour) || isNaN(minute)) {
          console.warn("parseTime: 时间字符串格式错误:", timeStr);
          return [0, 0];
        }
        return [hour, minute];
      } catch (error) {
        console.error("parseTime: 解析时间字符串时出错:", timeStr, error);
        return [0, 0];
      }
    },

    /**
     * 初始化交互状态 - 对应架构中的状态添加逻辑
     */
    initializeInteractionStates() {
      for (let i = 0; i < this.timeSlots.length; i++) {
        // 为每个日期下的时间段添加选中状态
        if (this.timeSlots[i].time && this.timeSlots[i].time.length > 0) {
          for (let j = 0; j < this.timeSlots[i].time.length; j++) {
            this.$set(this.timeSlots[i].time[j], "isOk", false); // 时间段未选中
          }
        }
        // 为每个日期添加选中状态
        this.$set(this.timeSlots[i], "isOk", false); // 日期未选中
      }
    },

    /**
     * 设置默认选择 - 对应架构中的默认选中逻辑
     */
    setDefaultSelection() {
      // 只有非全天服务才默认选中第一个工作日
      if (this.serviceInfo.intervalTime !== "4") {
        for (let i = 0; i < this.timeSlots.length; i++) {
          if (
            this.timeSlots[i].isWeekday &&
            this.timeSlots[i].time.length > 0
          ) {
            // 设置第一个工作日为选中状态
            this.$set(this.timeSlots[i], "isOk", true);

            // 深拷贝该日期的时间段到时间选择器
            this.timeQuantum = JSON.parse(
              JSON.stringify(this.timeSlots[i].time)
            );

            // 记录当前选中的日期
            this.timeQuantumData = this.timeSlots[i].date;
            this.selectedDate = this.timeSlots[i].date;

            break;
          }
        }
      }
    },

    /**
     * 获取时间间隔（分钟）
     */
    getIntervalMinutes() {
      const intervalMap = {
        1: 60, // 60分钟
        2: 120, // 120分钟
        3: 180, // 180分钟
        4: 1440, // 一天
        5: 60, // 一次/项，默认60分钟
      };
      return intervalMap[this.serviceInfo.intervalTime] || 60;
    },

    /**
     * 选择日期 - 按照架构文档的 selectDate 逻辑
     */
    selectDate(day) {
      if (!day.isWeekday) return; // 只有工作日才能点击

      if (this.serviceInfo.intervalTime === "4") {
        // 全天服务模式：切换选中状态（支持多选）
        const isCurrentlySelected = day.isOk;
        this.$set(day, "isOk", !isCurrentlySelected);

        // 重新筛选所有选中的日期
        this.selectedFullDays = this.filterSelectedDates();
      } else {
        // 时间段服务模式：单选日期
        if (day.time.length === 0) return; // 没有可用时间段则不能选择

        // 更新所有日期的选中状态（单选）
        for (let i = 0; i < this.timeSlots.length; i++) {
          if (this.timeSlots[i].date === day.date) {
            this.$set(this.timeSlots[i], "isOk", true); // 设置当前日期为选中

            // 深拷贝当前日期的时间段到时间选择器
            this.timeQuantum = JSON.parse(
              JSON.stringify(this.timeSlots[i].time)
            );
            this.timeQuantumData = day.date; // 记录当前选中日期
            this.selectedDate = day.date;
          } else {
            this.$set(this.timeSlots[i], "isOk", false); // 其他日期取消选中
          }
        }
      }
    },

    /**
     * 筛选选中的日期 - 对应架构中的 filterDates 函数
     */
    filterSelectedDates() {
      return this.timeSlots
        .filter((item) => item.isOk) // 只保留选中的日期
        .map((item) => item.date); // 提取日期字符串
    },

    /**
     * 筛选可用时间 - 对应架构中的 filterAvailableTimes 函数
     */
    filterAvailableTimes() {
      const result = [];

      // 遍历所有日期
      this.timeSlots.forEach((item) => {
        if (item.time && item.time.length > 0) {
          // 遍历该日期下的所有时间段
          item.time.forEach((timeSlot) => {
            if (timeSlot.isOk) {
              // 只收集选中的时间段
              result.push({
                type: "time",
                date: item.date,
                time: timeSlot.value,
              });
            }
          });
        }
      });

      return result;
    },

    /**
     * 切换时间槽选择状态 - 按照架构文档的 selectItem 逻辑
     */
    toggleTimeSlot(date, timeSlot) {
      if (!timeSlot.isTimeQuantum) return; // 只有可预约的时间段才能选择

      // 找到 timeQuantum 中对应的时间段
      const timeQuantumItem = this.timeQuantum.find(
        (item) => item.value === timeSlot.value
      );
      if (!timeQuantumItem) return;

      // 切换时间段选中状态（支持多选）
      const isCurrentlySelected = timeQuantumItem.isOk;
      this.$set(timeQuantumItem, "isOk", !isCurrentlySelected);

      // 同步更新主数据源中对应日期的时间段状态
      for (let i = 0; i < this.timeSlots.length; i++) {
        if (this.timeSlots[i].date === this.timeQuantumData) {
          // 将时间选择器的状态同步回主数据源
          this.timeSlots[i].time = JSON.parse(JSON.stringify(this.timeQuantum));
          break;
        }
      }

      // 重新生成已选择的时间段列表
      this.selectedTimeSlots = this.filterAvailableTimes();
    },

    /**
     * 检查时间槽是否已选择 - 基于 timeQuantum 状态
     */
    isTimeSlotSelected(date, time) {
      if (date !== this.timeQuantumData) return false;
      const timeItem = this.timeQuantum.find((item) => item.value === time);
      return timeItem ? timeItem.isOk : false;
    },

    /**
     * 移除选择的时间槽 - 按照架构文档的 deleteSelectItem 逻辑
     */
    removeTimeSlot(index) {
      const timeSlotToRemove = this.selectedTimeSlots[index];
      if (!timeSlotToRemove) return;

      // 从已选择列表中删除指定项
      this.selectedTimeSlots.splice(index, 1);

      // 同步更新主数据源中对应的时间段状态
      for (let i = 0; i < this.timeSlots.length; i++) {
        if (this.timeSlots[i].date === timeSlotToRemove.date) {
          // 找到对应日期，更新时间段状态
          this.timeSlots[i].time.forEach((element, timeIndex) => {
            if (element.value === timeSlotToRemove.time) {
              this.$set(this.timeSlots[i].time[timeIndex], "isOk", false); // 取消选中
            }
          });

          // 如果当前显示的是这个日期，同步更新 timeQuantum
          if (this.timeQuantumData === timeSlotToRemove.date) {
            this.timeQuantum = JSON.parse(
              JSON.stringify(this.timeSlots[i].time)
            );
          }
          break;
        }
      }
    },

    /**
     * 清空所有选择 - 重置所有状态
     */
    clearAllSelections() {
      if (this.serviceInfo.intervalTime === "4") {
        // 全天服务：清空日期选择
        this.selectedFullDays = [];
        // 重置所有日期的选中状态
        this.timeSlots.forEach((day) => {
          this.$set(day, "isOk", false);
        });
      } else {
        // 时间段服务：清空时间段选择
        this.selectedTimeSlots = [];
        // 重置所有时间段的选中状态
        this.timeSlots.forEach((day) => {
          if (day.time && day.time.length > 0) {
            day.time.forEach((timeSlot) => {
              this.$set(timeSlot, "isOk", false);
            });
          }
        });
        // 同步更新当前显示的时间段
        if (this.timeQuantumData) {
          const currentDay = this.timeSlots.find(
            (d) => d.date === this.timeQuantumData
          );
          if (currentDay) {
            this.timeQuantum = JSON.parse(JSON.stringify(currentDay.time));
          }
        }
      }
    },

    /**
     * 切换全天日期选择状态
     */
    toggleFullDaySelection(day) {
      if (!day.isWeekday) return;

      const dateStr = day.date;
      const existingIndex = this.selectedFullDays.findIndex(
        (date) => date === dateStr
      );

      if (existingIndex >= 0) {
        // 取消选择
        this.selectedFullDays.splice(existingIndex, 1);
      } else {
        // 添加选择
        this.selectedFullDays.push(dateStr);
      }
    },

    /**
     * 检查全天日期是否已选择
     */
    isFullDaySelected(date) {
      return this.selectedFullDays.includes(date);
    },

    /**
     * 移除全天日期
     */
    removeFullDay(index) {
      this.selectedFullDays.splice(index, 1);
    },

    /**
     * 单个预约日期变化处理
     */
    onSingleDateChange(date) {
      console.log("日期选择变化:", {
        新选择的日期: date,
        服务模式: this.serviceInfo.orderType,
        服务信息: this.serviceInfo,
      });
      this.singleAppointment.time = "";

      // 强制触发计算属性重新计算
      this.$nextTick(() => {
        if (this.serviceInfo.orderType === "2") {
          const available = this.availableTimesForSingleDate;
          console.log("同时多单模式 - 日期变化后的详细状态:", {
            可用时间总数: available.length,
            可选择时间数: available.filter((t) => t.isTimeQuantum).length,
            已预约时间数: available.filter((t) => t.isBooked).length,
            已过期时间数: available.filter((t) => t.isPast).length,
            时间选择器禁用状态: !available.length,
            完整时间列表: available,
          });
        }
      });
    },

    /**
     * 将完整日期格式转换为短格式
     * 例如: "2024-09-08" → "9-8"
     */
    formatDateToShort(fullDate) {
      if (!fullDate) return "";

      console.log("formatDateToShort 输入:", fullDate);

      // 处理 "yyyy-MM-dd" 格式
      if (fullDate.includes("-") && fullDate.length === 10) {
        const parts = fullDate.split("-");
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const day = parseInt(parts[2]);

        // 检查年份是否为当前年份
        const currentYear = new Date().getFullYear();
        if (year !== currentYear) {
          console.warn("formatDateToShort: 选择的年份与当前年份不匹配", {
            选择年份: year,
            当前年份: currentYear,
          });
        }

        const result = `${month}-${day}`;
        console.log("formatDateToShort 输出:", result);
        return result;
      }

      // 如果已经是短格式，直接返回
      console.log("formatDateToShort 已是短格式，直接返回:", fullDate);
      return fullDate;
    },

    /**
     * 将短格式日期转换为完整格式
     * 例如: "9-8" → "2024-09-08"
     */
    formatDateToFull(shortDate) {
      if (!shortDate) return "";

      // 处理 "M-d" 格式
      if (shortDate.includes("-") && shortDate.length <= 5) {
        const currentYear = new Date().getFullYear();
        const parts = shortDate.split("-");
        const month = parts[0].padStart(2, "0");
        const day = parts[1].padStart(2, "0");
        return `${currentYear}-${month}-${day}`;
      }

      // 如果已经是完整格式，直接返回
      return shortDate;
    },
  },
};
</script>

<style lang="scss" scoped>
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;
$primary-red-light: #f8d7da;

.advanced-appointment {
  .appointment-type-tabs {
    margin-bottom: 16px;

    .type-tab-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid rgba(231, 76, 60, 0.15);

      h4 {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;

        i {
          color: $primary-red;
          font-size: 16px;
        }
      }

      .order-type-info {
        .order-type-badge {
          padding: 4px 10px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;

          &.single-order {
            background: rgba(231, 76, 60, 0.1);
            color: $primary-red;
            border: 1px solid rgba(231, 76, 60, 0.2);
          }

          &.multiple-order {
            background: rgba(52, 152, 219, 0.1);
            color: #3498db;
            border: 1px solid rgba(52, 152, 219, 0.2);
          }
        }
      }
    }
  }

  // 多时间段选择模式
  .multiple-time-selection {
    .time-selection-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding: 10px 12px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 6px;
      border: 1px solid rgba(231, 76, 60, 0.1);

      .selection-info {
        display: flex;
        gap: 12px;

        .selected-count {
          font-weight: 600;
          color: $primary-red;
          font-size: 14px;
        }

        .duration-info {
          color: #666;
          font-size: 13px;
        }
      }

      .clear-selection-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        background: rgba(231, 76, 60, 0.1);
        color: $primary-red;
        border: 1px solid rgba(231, 76, 60, 0.2);
        border-radius: 4px;
        font-size: 11px;
        cursor: pointer;

        &:hover {
          background: $primary-red;
          color: white;
        }
      }
    }

    // 日期滚动选择
    .date-scroll-container {
      margin-bottom: 16px;
      width: 100%;
      max-width: 600px;

      .date-scroll-wrapper {
        display: flex;
        gap: 8px;
        overflow-x: auto;
        overflow-y: hidden;
        padding: 6px 2px;
        width: 100%;

        // 滚动条样式
        &::-webkit-scrollbar {
          height: 4px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(231, 76, 60, 0.4);
          border-radius: 2px;

          &:hover {
            background: rgba(231, 76, 60, 0.6);
          }
        }

        .date-item {
          flex: 0 0 auto;
          width: 70px;
          padding: 8px 6px;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(231, 76, 60, 0.1);
          border-radius: 8px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;

          .date-number {
            font-size: 16px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 2px;
          }

          .date-week {
            font-size: 11px;
            color: #666;
            margin-bottom: 4px;
          }

          .date-status {
            font-size: 10px;

            .status-selected {
              color: #e74c3c;
              font-weight: 600;
            }

            .status-available {
              color: #27ae60;
              font-weight: 500;
            }

            .status-full {
              color: #e74c3c;
            }

            .status-closed {
              color: #95a5a6;
            }
          }

          &.active {
            border-color: $primary-red;
            background: rgba(231, 76, 60, 0.1);
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(231, 76, 60, 0.15);
          }

          &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          &:not(.disabled):not(.active):hover {
            border-color: rgba(231, 76, 60, 0.3);
          }
        }
      }
    }

    // 时间网格
    .time-grid-container {
      margin-bottom: 16px;
      width: 100%;
      max-width: 600px;

      .time-grid-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        h5 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #2c3e50;
        }

        .time-legend {
          display: flex;
          gap: 8px;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 3px;
            font-size: 11px;
            color: #666;

            .legend-dot {
              width: 6px;
              height: 6px;
              border-radius: 50%;
            }

            &.available .legend-dot {
              background: #27ae60;
            }

            &.booked .legend-dot {
              background: #e74c3c;
            }

            &.past .legend-dot {
              background: #95a5a6;
            }
          }
        }
      }

      .time-grid {
        display: flex;
        flex-wrap: nowrap;
        gap: 6px;
        overflow-x: auto;
        overflow-y: hidden;
        padding: 6px 2px;
        width: 100%;

        // 滚动条样式
        &::-webkit-scrollbar {
          height: 4px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(231, 76, 60, 0.4);
          border-radius: 2px;

          &:hover {
            background: rgba(231, 76, 60, 0.6);
          }
        }

        .time-slot {
          position: relative;
          flex: 0 0 auto;
          width: 80px;
          min-width: 80px;
          padding: 8px 6px;
          border-radius: 6px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid transparent;

          .time-text {
            font-size: 12px;
            font-weight: 500;
          }

          .selected-icon {
            position: absolute;
            top: 2px;
            right: 2px;
            font-size: 10px;
            color: white;
          }

          &.available {
            background: rgba(39, 174, 96, 0.1);
            border-color: rgba(39, 174, 96, 0.2);
            color: #27ae60;

            &:hover {
              background: rgba(39, 174, 96, 0.2);
            }
          }

          &.booked {
            background: rgba(231, 76, 60, 0.1);
            border-color: rgba(231, 76, 60, 0.2);
            color: #e74c3c;
            cursor: not-allowed;
          }

          &.past {
            background: rgba(149, 165, 166, 0.1);
            border-color: rgba(149, 165, 166, 0.2);
            color: #95a5a6;
            cursor: not-allowed;
          }

          &.selected {
            background: $primary-red;
            border-color: $primary-red;
            color: white;
            transform: translateY(-1px);
            box-shadow: 0 2px 6px rgba(231, 76, 60, 0.2);
          }
        }
      }
    }

    // 已选择时间列表
    .selected-times-list {
      margin-top: 16px;
      padding: 12px;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 8px;
      border: 1px solid rgba(231, 76, 60, 0.1);
      width: 100%;
      max-width: 600px;

      h5 {
        margin: 0 0 8px 0;
        font-size: 13px;
        font-weight: 600;
        color: #2c3e50;
      }

      .selected-times-grid {
        display: flex;
        flex-wrap: nowrap;
        gap: 6px;
        overflow-x: auto;
        overflow-y: hidden;
        padding: 2px 0;

        // 滚动条样式
        &::-webkit-scrollbar {
          height: 4px;
        }

        &::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(231, 76, 60, 0.4);
          border-radius: 2px;

          &:hover {
            background: rgba(231, 76, 60, 0.6);
          }
        }

        .selected-time-item {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: $primary-red;
          color: white;
          border-radius: 12px;
          font-size: 11px;
          flex: 0 0 auto;
          white-space: nowrap;

          .remove-btn {
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            padding: 0;
            font-size: 10px;

            &:hover {
              opacity: 0.8;
            }
          }
        }
      }
    }
  }

  // 单时间选择模式
  .single-time-selection {
    .single-selection-form {
      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;

        .form-group {
          label {
            display: block;
            margin-bottom: 6px;
            font-weight: 600;
            color: #2c3e50;
            font-size: 13px;
          }
        }
      }
    }
  }

  // 全天选择模式
  .full-day-selection {
    .full-day-info {
      margin-bottom: 12px;

      .info-card {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px;
        background: rgba(52, 152, 219, 0.1);
        border: 1px solid rgba(52, 152, 219, 0.2);
        border-radius: 8px;

        i {
          font-size: 20px;
          color: #3498db;
        }

        .info-content {
          h5 {
            margin: 0 0 3px 0;
            font-size: 14px;
            font-weight: 600;
            color: #2c3e50;
          }

          p {
            margin: 0;
            font-size: 12px;
            color: #666;
          }
        }
      }
    }

    .full-day-picker {
      text-align: center;
    }
  }

  // 预约摘要
  .appointment-summary {
    margin-top: 18px;
    padding: 14px;
    background: rgba(231, 76, 60, 0.05);
    border: 1px solid rgba(231, 76, 60, 0.15);
    border-radius: 8px;

    .summary-header {
      margin-bottom: 12px;

      h5 {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: #2c3e50;

        i {
          color: $primary-red;
          font-size: 14px;
        }
      }
    }

    .summary-content {
      .summary-times {
        margin-bottom: 10px;

        .times-list {
          margin-top: 6px;
          display: flex;
          flex-wrap: nowrap;
          gap: 4px;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 2px 0;

          // 滚动条样式
          &::-webkit-scrollbar {
            height: 3px;
          }

          &::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.05);
            border-radius: 2px;
          }

          &::-webkit-scrollbar-thumb {
            background: rgba(231, 76, 60, 0.4);
            border-radius: 2px;

            &:hover {
              background: rgba(231, 76, 60, 0.6);
            }
          }

          .time-tag {
            padding: 3px 6px;
            background: rgba(231, 76, 60, 0.1);
            color: $primary-red;
            border-radius: 6px;
            font-size: 11px;
            font-weight: 500;
            flex: 0 0 auto;
            white-space: nowrap;
          }
        }
      }

      .summary-price,
      .summary-quantity {
        display: flex;
        justify-content: space-between;
        margin-bottom: 6px;

        .price-label,
        .quantity-label {
          color: #666;
          font-size: 13px;
        }

        .price-value {
          font-weight: 600;
          color: $primary-red;
          font-size: 15px;
        }

        .quantity-value {
          font-weight: 600;
          color: #2c3e50;
          font-size: 13px;
        }
      }
    }
  }
}

// Element UI 组件样式覆盖
::v-deep .el-date-editor,
::v-deep .el-select {
  width: 100%;

  .el-input__inner {
    border-radius: 6px;
    border-color: rgba(231, 76, 60, 0.2);
    height: 36px;
    font-size: 13px;

    &:focus {
      border-color: $primary-red;
    }
  }
}

::v-deep .el-input--small .el-input__inner {
  height: 32px;
  font-size: 12px;
}
</style>
